import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Checkbox,
  Button,
  Container,
  FormControlLabel,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import MicNoneIcon from "@mui/icons-material/MicNone";
import StopCircleIcon from "@mui/icons-material/StopCircle";
import HeadphonesIcon from "@mui/icons-material/Headphones";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";

import { useAuth } from "../context/AuthContext";
import { authFetch, getApiErrorMessage, throwForResponse } from "../utils/apiClient";
import { apiUrl } from "../utils/apiBaseUrl";
import { readSavedInterviewConfig } from "../utils/accessMode";

export const FINISH_TIMEOUT_MS = 20000;
const FACE_DETECTION_INTERVAL_MS = 1000;

const BUSY_STATES = new Set(["starting", "speaking", "processing", "finishing"]);

function recorderMimeType() {
  if (typeof MediaRecorder === "undefined" || typeof MediaRecorder.isTypeSupported !== "function") {
    return "";
  }
  return ["audio/webm;codecs=opus", "audio/webm", "audio/ogg;codecs=opus"]
    .find((type) => MediaRecorder.isTypeSupported(type)) || "";
}

async function readVoiceResponse(response) {
  await throwForResponse(response, { defaultMessage: "Voice interview is unavailable." });
  const payload = await response.json();
  if (!payload?.audio_base64) {
    throw new Error("The interviewer audio was empty. Retry the turn.");
  }
  return payload;
}

function audioDataUrl(payload) {
  return `data:${payload.audio_content_type || "audio/mpeg"};base64,${payload.audio_base64}`;
}

function hasLiveVideoTrack(stream) {
  return Boolean(stream?.getVideoTracks?.().some((track) => track.readyState !== "ended"));
}

function audioRecordingStream(stream) {
  if (!stream?.getVideoTracks?.().length) return stream;
  if (typeof window.MediaStream !== "function") {
    throw new Error("This browser cannot keep camera data separate from your audio.");
  }
  return new window.MediaStream(stream.getAudioTracks?.() || []);
}

export default function VoiceInterviewPage() {
  const { sessionId: routeSessionId, type: routeType } = useParams();
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const sessionId = useMemo(
    () => routeSessionId || `session_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
    [routeSessionId],
  );
  const config = useMemo(() => {
    const saved = readSavedInterviewConfig(sessionStorage)?.config || {};
    return { ...saved, type: routeType || saved.type || "mixed" };
  }, [routeType]);

  const [status, setStatus] = useState("ready");
  const [hasStarted, setHasStarted] = useState(false);
  const [answerCount, setAnswerCount] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [failedRequest, setFailedRequest] = useState(null);
  const [faceCheckEnabled, setFaceCheckEnabled] = useState(false);
  const [faceCheckStatus, setFaceCheckStatus] = useState("off");
  const [facePresence, setFacePresence] = useState("unknown");
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const audioRef = useRef(null);
  const failedAudioRef = useRef(null);
  const controllerRef = useRef(null);
  const faceVideoRef = useRef(null);
  const faceDetectorRef = useRef(null);
  const faceDetectionTimerRef = useRef(null);
  const faceDetectionInFlightRef = useRef(false);
  const mountedRef = useRef(true);
  const startedAtRef = useRef(Date.now());

  const stopFacePresenceCheck = useCallback(() => {
    if (faceDetectionTimerRef.current) {
      clearInterval(faceDetectionTimerRef.current);
      faceDetectionTimerRef.current = null;
    }
    faceDetectorRef.current = null;
    faceDetectionInFlightRef.current = false;
    if (faceVideoRef.current) {
      try {
        faceVideoRef.current.pause?.();
      } catch {
        // The stream is still cleaned up by the owning media tracks.
      }
      faceVideoRef.current.srcObject = null;
    }
  }, []);

  const ensureMediaStream = useCallback(async (includeFaceCheck = false) => {
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      throw new Error("Voice interviews need a browser with microphone recording support.");
    }

    const hasVideo = hasLiveVideoTrack(streamRef.current);
    if (streamRef.current && hasVideo === includeFaceCheck) {
      return streamRef.current;
    }

    streamRef.current?.getTracks?.().forEach((track) => track.stop());
    streamRef.current = await navigator.mediaDevices.getUserMedia({
      audio: true,
      ...(includeFaceCheck ? { video: true } : {}),
    });
    return streamRef.current;
  }, []);

  const startFacePresenceCheck = useCallback(async (stream) => {
    if (!faceCheckEnabled) return;

    const video = faceVideoRef.current;
    const Detector = window.FaceDetector;
    if (!video || !hasLiveVideoTrack(stream)) {
      setFaceCheckStatus("unavailable");
      setFacePresence("unavailable");
      return;
    }
    if (typeof Detector !== "function") {
      setFaceCheckStatus("unsupported");
      setFacePresence("unavailable");
      stream.getVideoTracks?.().forEach((track) => track.stop());
      return;
    }

    try {
      const detector = new Detector({ fastMode: true, maxDetectedFaces: 1 });
      video.srcObject = stream;
      await video.play();
      if (!mountedRef.current) return;
      faceDetectorRef.current = detector;
      setFaceCheckStatus("active");

      const detectFace = async () => {
        if (
          !mountedRef.current
          || !faceDetectorRef.current
          || faceDetectionInFlightRef.current
          || video.readyState < 2
        ) return;
        faceDetectionInFlightRef.current = true;
        try {
          const faces = await faceDetectorRef.current.detect(video);
          if (mountedRef.current) setFacePresence(faces.length ? "detected" : "not_detected");
        } catch {
          if (mountedRef.current) {
            setFaceCheckStatus("unavailable");
            setFacePresence("unavailable");
          }
          stopFacePresenceCheck();
          stream.getVideoTracks?.().forEach((track) => track.stop());
        } finally {
          faceDetectionInFlightRef.current = false;
        }
      };

      faceDetectionTimerRef.current = setInterval(detectFace, FACE_DETECTION_INTERVAL_MS);
      detectFace();
    } catch {
      setFaceCheckStatus("unavailable");
      setFacePresence("unavailable");
      stopFacePresenceCheck();
      stream.getVideoTracks?.().forEach((track) => track.stop());
    }
  }, [faceCheckEnabled, stopFacePresenceCheck]);

  const playInterviewerAudio = useCallback((payload) => {
    if (typeof Audio === "undefined") {
      throw new Error("This browser cannot play interviewer audio.");
    }
    const audio = new Audio(audioDataUrl(payload));
    audio.preload = "auto";
    audioRef.current = audio;
    setStatus("speaking");
    return new Promise((resolve) => {
      let finished = false;
      const finish = () => {
        if (finished) return;
        finished = true;
        if (audioRef.current === audio) audioRef.current = null;
        if (mountedRef.current) setStatus("listening");
        resolve();
      };
      audio.onended = finish;
      audio.onerror = () => {
        if (mountedRef.current) setError("Interviewer audio could not play. Check your volume and retry.");
        finish();
      };
      Promise.resolve(audio.play()).catch(() => {
        if (mountedRef.current) setError("Interviewer audio was blocked. Tap Start interview again to enable it.");
        finish();
      });
    });
  }, []);

  const requestOpening = useCallback(async () => {
    setError("");
    setFailedRequest(null);
    setStatus("starting");
    const controller = new AbortController();
    controllerRef.current = controller;
    try {
      let mediaStream;
      const faceDetectionSupported = typeof window.FaceDetector === "function";
      const includeFaceCheck = faceCheckEnabled && faceDetectionSupported;
      if (faceCheckEnabled && !faceDetectionSupported) {
        setFaceCheckStatus("unsupported");
        setFacePresence("unavailable");
      }
      try {
        mediaStream = await ensureMediaStream(includeFaceCheck);
      } catch (mediaError) {
        if (!includeFaceCheck) throw mediaError;
        setFaceCheckStatus("unavailable");
        setFacePresence("unavailable");
        mediaStream = await ensureMediaStream(false);
      }
      const token = await getToken();
      const response = await authFetch(apiUrl(`/api/interview/${sessionId}/voice/opening`), token, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        signal: controller.signal,
        body: JSON.stringify({
          last_user_turn: "",
          interviewType: config.type || "mixed",
          difficulty: config.difficulty || "auto",
          role: config.role,
          company: config.company,
          questionMix: config.questionMix || "balanced",
          interviewStyle: config.interviewStyle || "neutral",
          durationMinutes: config.duration || 10,
          selectedSkills: config.selectedSkills || [],
        }),
      });
      const payload = await readVoiceResponse(response);
      if (!mountedRef.current) return;
      setHasStarted(true);
      startedAtRef.current = Date.now();
      if (includeFaceCheck) await startFacePresenceCheck(mediaStream);
      await playInterviewerAudio(payload);
    } catch (requestError) {
      if (!mountedRef.current) return;
      setStatus("ready");
      setFailedRequest({ type: "opening" });
      setError(getApiErrorMessage(requestError, { defaultMessage: "Unable to start the voice interview." }));
    } finally {
      if (controllerRef.current === controller) controllerRef.current = null;
    }
  }, [config, ensureMediaStream, faceCheckEnabled, getToken, playInterviewerAudio, sessionId, startFacePresenceCheck]);

  const requestVoiceTurn = useCallback(async (recording) => {
    if (!recording?.size) {
      setStatus("listening");
      setError("No audio was captured. Try recording your answer again.");
      return;
    }
    setError("");
    setFailedRequest(null);
    setStatus("processing");
    const controller = new AbortController();
    controllerRef.current = controller;
    const form = new FormData();
    const filename = recording.type.includes("ogg") ? "candidate-answer.ogg" : "candidate-answer.webm";
    form.append("audio", recording, filename);
    try {
      const token = await getToken();
      const response = await authFetch(apiUrl(`/api/interview/${sessionId}/voice/turn`), token, {
        method: "POST",
        signal: controller.signal,
        body: form,
      });
      const payload = await readVoiceResponse(response);
      if (!mountedRef.current) return;
      setAnswerCount((count) => count + 1);
      failedAudioRef.current = null;
      await playInterviewerAudio(payload);
    } catch (requestError) {
      if (!mountedRef.current) return;
      failedAudioRef.current = recording;
      setStatus("listening");
      setFailedRequest({ type: "turn" });
      setError(getApiErrorMessage(requestError, { defaultMessage: "Unable to process that answer." }));
    } finally {
      if (controllerRef.current === controller) controllerRef.current = null;
    }
  }, [getToken, playInterviewerAudio, sessionId]);

  const startRecording = () => {
    if (!hasStarted || status !== "listening" || saving) return;
    setError("");
    setFailedRequest(null);
    try {
      const mimeType = recorderMimeType();
      const recorder = mimeType
        ? new MediaRecorder(audioRecordingStream(streamRef.current), { mimeType })
        : new MediaRecorder(audioRecordingStream(streamRef.current));
      const chunks = [];
      recorder.ondataavailable = (event) => {
        if (event.data?.size) chunks.push(event.data);
      };
      recorder.onstop = () => {
        const recording = new Blob(chunks, { type: mimeType || "audio/webm" });
        requestVoiceTurn(recording);
      };
      recorder.onerror = () => {
        setStatus("listening");
        setError("Microphone recording failed. Check browser permissions and try again.");
      };
      recorderRef.current = recorder;
      recorder.start();
      setStatus("recording");
    } catch (recordingError) {
      setError(getApiErrorMessage(recordingError, { defaultMessage: "Unable to start microphone recording." }));
    }
  };

  const stopRecording = () => {
    if (recorderRef.current?.state === "recording") recorderRef.current.stop();
  };

  const finishInterview = async () => {
    if (!answerCount || saving || BUSY_STATES.has(status) || status === "recording") return;
    setError("");
    setFailedRequest(null);
    setSaving(true);
    setStatus("finishing");
    const controller = new AbortController();
    controllerRef.current = controller;
    let timeoutId;
    try {
      const token = await getToken();
      const request = authFetch(apiUrl(`/api/interview/${sessionId}/complete`), token, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        signal: controller.signal,
        body: JSON.stringify({
          interview_mode: "sarvam_voice",
          questions_answered: answerCount,
          interview_type: config.type || "mixed",
          duration_minutes: Math.max(1, Math.round((Date.now() - startedAtRef.current) / 60000)),
          meta: { role: config.role, company: config.company, selected_skills: config.selectedSkills || [] },
        }),
      });
      const timeout = new Promise((_, reject) => {
        timeoutId = setTimeout(() => {
          controller.abort();
          reject(new Error("Report generation took too long. Retry or cancel to continue."));
        }, FINISH_TIMEOUT_MS);
      });
      const response = await Promise.race([request, timeout]);
      await throwForResponse(response, { defaultMessage: "Unable to generate the interview report." });
      const result = await response.json();
      navigate(result.report_id ? `/report/${result.report_id}` : "/interviews");
    } catch (requestError) {
      if (!mountedRef.current) return;
      setStatus("listening");
      setFailedRequest({ type: "finish" });
      setError(getApiErrorMessage(requestError, { defaultMessage: "Unable to generate the interview report." }));
    } finally {
      clearTimeout(timeoutId);
      setSaving(false);
      if (controllerRef.current === controller) controllerRef.current = null;
    }
  };

  const retryFailedRequest = () => {
    if (failedRequest?.type === "opening") requestOpening();
    if (failedRequest?.type === "turn" && failedAudioRef.current) requestVoiceTurn(failedAudioRef.current);
    if (failedRequest?.type === "finish") finishInterview();
  };

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      controllerRef.current?.abort();
      audioRef.current?.pause?.();
      stopFacePresenceCheck();
      streamRef.current?.getTracks?.().forEach((track) => track.stop());
    };
  }, [stopFacePresenceCheck]);

  const busy = BUSY_STATES.has(status);
  const statusText = {
    ready: "Start when you are ready.",
    starting: "Getting the interviewer ready…",
    speaking: "The interviewer is speaking…",
    listening: "Your microphone is ready.",
    recording: "Listening to your answer…",
    processing: "Processing your answer…",
    finishing: "Generating your coaching report…",
  }[status] || "Ready when you are.";
  const primaryLabel = !hasStarted
    ? "Start interview"
    : status === "recording" ? "Stop answer" : "Start answer";
  const faceCheckMessage = !faceCheckEnabled
    ? "Optional: use your camera for a private, local face-presence check."
    : faceCheckStatus === "unsupported"
      ? "This browser cannot run local face detection. The interview will continue audio-only."
      : faceCheckStatus === "unavailable"
        ? "Camera access or local detection was unavailable. The interview will continue audio-only."
        : facePresence === "detected"
          ? "Face presence detected locally. No video or face results are uploaded."
          : facePresence === "not_detected"
            ? "No face detected right now. Camera data stays on this device."
            : faceCheckStatus === "active"
              ? "Private face check is active on this device. No video or face results are uploaded."
              : "Camera stays on this device; no video or face results are uploaded.";

  return (
    <Box sx={{ minHeight: "100dvh", bgcolor: "#f7f8fc", py: { xs: 3, md: 6 } }}>
      <Container maxWidth="sm">
        <Stack spacing={3}>
          <Box>
            <Typography variant="overline" color="text.secondary">Voice interview</Typography>
            <Typography component="h1" variant="h4" sx={{ fontWeight: 800 }}>Practice interview</Typography>
            <Typography color="text.secondary" sx={{ mt: 1 }}>
              The interviewer will speak each prompt. Respond naturally using your microphone.
            </Typography>
          </Box>

          {error && (
            <Alert severity="error">
              <Stack spacing={1}>
                <Typography component="div">{error}</Typography>
                {failedRequest && (
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                    <Button size="small" variant="contained" onClick={retryFailedRequest} disabled={busy || saving}>Retry</Button>
                    <Button size="small" variant="outlined" onClick={() => { setError(""); setFailedRequest(null); }} disabled={busy || saving}>Dismiss</Button>
                  </Stack>
                )}
              </Stack>
            </Alert>
          )}

          <Paper component="section" aria-label="Voice interview room" sx={{ p: { xs: 3, md: 5 }, textAlign: "center" }}>
            <Stack spacing={2.5} alignItems="center">
              <Box sx={{ width: 104, height: 104, borderRadius: "50%", display: "grid", placeItems: "center", bgcolor: status === "recording" ? "error.main" : "primary.main", color: "primary.contrastText" }}>
                {status === "recording" ? <StopCircleIcon sx={{ fontSize: 48 }} /> : <HeadphonesIcon sx={{ fontSize: 48 }} />}
              </Box>
              <Typography role="status" aria-live="polite" variant="h6">{statusText}</Typography>
              <Typography color="text.secondary" variant="body2">
                Your spoken answers are used for the coaching report. Raw audio is not retained.
              </Typography>
              <Box sx={{ width: "100%", textAlign: "left" }}>
                <FormControlLabel
                  control={(
                    <Checkbox
                      checked={faceCheckEnabled}
                      onChange={(event) => {
                        setFaceCheckEnabled(event.target.checked);
                        if (!event.target.checked) {
                          setFaceCheckStatus("off");
                          setFacePresence("unknown");
                        }
                      }}
                      disabled={hasStarted || busy || saving}
                      inputProps={{ "aria-describedby": "face-check-help" }}
                    />
                  )}
                  label="Enable optional private face-presence check"
                />
                <Typography id="face-check-help" color="text.secondary" variant="caption" sx={{ display: "block", pl: 4.5 }}>
                  <VideocamOutlinedIcon aria-hidden="true" sx={{ fontSize: 16, verticalAlign: "text-bottom", mr: 0.5 }} />
                  {faceCheckMessage}
                </Typography>
              </Box>
              <Button
                variant="contained"
                size="large"
                startIcon={status === "recording" ? <StopCircleIcon /> : <MicNoneIcon />}
                onClick={status === "recording" ? stopRecording : (hasStarted ? startRecording : requestOpening)}
                disabled={busy || saving || (hasStarted && status !== "listening" && status !== "recording")}
                sx={{ minWidth: 190 }}
              >
                {primaryLabel}
              </Button>
              {faceCheckEnabled && (
                <Box
                  component="video"
                  ref={faceVideoRef}
                  autoPlay
                  muted
                  playsInline
                  aria-hidden="true"
                  tabIndex={-1}
                  sx={{ position: "absolute", width: 1, height: 1, opacity: 0, pointerEvents: "none" }}
                />
              )}
            </Stack>
          </Paper>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} justifyContent="flex-end">
            <Button variant="outlined" onClick={finishInterview} disabled={!answerCount || saving || busy || status === "recording"}>
              {saving ? "Generating report…" : "Finish and generate report"}
            </Button>
          </Stack>
        </Stack>
      </Container>
    </Box>
  );
}
